import { cn } from "@/shared/lib/cn";

/**
 * A small set of mutually-exclusive options on one soft filled track (no
 * border). The active segment is a raised paper chip — selection is carried by
 * elevation, not a hue. (Settings → Appearance theme picker.)
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional colour swatch (any CSS colour, e.g. a `var(--swatch-*)`) shown before the label. */
  dot?: string;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  "aria-label": string;
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  "aria-label": ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex gap-0.5 rounded-pill bg-bubble p-[3px]"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-pill px-3.5 py-1.5 font-sans text-[13px] font-medium outline-none transition-[background,color,box-shadow] duration-150 ease-house focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring)]",
              active
                ? "bg-[var(--raised)] text-ink shadow-[var(--raised-shadow)]"
                : "text-ink hover:bg-[var(--raised-hover)]",
            )}
          >
            {option.dot ? (
              <span
                aria-hidden="true"
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: option.dot }}
              />
            ) : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
